import { useState } from "react";
import { useResources } from "../../hooks/useResources";
import { usePermissions } from "../../hooks/usePermissions";
import type {
  JobQuestion,
  QuestionStatus,
} from "../../interfaces/componentInterfaces/jobDetails";
import ContentHeader from "../contentHeader/ContentHeader";
import SearchInput from "../searchInput/SearchInput";
import ScrollbarArrows from "../table/ScrollbarArrows";

interface JobQuestionsTableProps {
  questions: JobQuestion[];
  onPreview?: (questionId: string) => void;
  onDelete?: (questionId: string) => void;
}

const JobQuestionsTable = ({
  questions,
  onPreview,
  onDelete,
}: JobQuestionsTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { getResourceValue } = useResources();
  const { canDeleteUnpublishedQuestions } = usePermissions();


  const filteredQuestions = questions.filter((q) =>
    q.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusClass = (status: QuestionStatus): string => {
    const statusMap: Record<QuestionStatus, string> = {
      Uploaded: "status-uploaded",
      "Pending Graphics": "status-pending",
      Failed: "status-failed",
      Published: "status-published",
    };
    return statusMap[status] || "";
  };

  return (
    <>
      <ContentHeader
        title={getResourceValue("job_questions_title", "Job Questions")}
        headingLevel={2}
        showActions={false}
        className="content-header--section-title"
      />
      <div className="job-details__questions-section">
        <SearchInput
          value={searchQuery}
          onChange={(value: string) => setSearchQuery(value)}
          placeholder={getResourceValue("search_placeholder", "Search by Question ID")}
        />

        <ScrollbarArrows
          targetId="job-questions-table-scroll"
          tablesScroll={3}
        />
        <div className="data-table-wrapper" id="job-questions-table-scroll">
          {filteredQuestions.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{getResourceValue("question_id", "Question ID")}</th>
                  <th>
                    {getResourceValue("question_status", "Question Status")}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredQuestions.map((question, index) => (
                  <tr key={index}>
                    <td>{question.id}</td>
                    <td>
                      <span
                        className={`job-details__question-status ${getStatusClass(
                          question.status
                        )}`}
                      >
                        {getResourceValue(
                          question.status.toLowerCase().replace(" ", "_"),
                          question.status
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className={`btn btn-green ${
                            question.status === "Failed"
                              ? "btn--hidden"
                              : ""
                          }`}
                          onClick={() => onPreview?.(question.id)}
                          disabled={question.status === "Failed"}
                        >
                          <i className="f-icon eye-icon"></i>
                          {getResourceValue("preview", "Preview")}
                        </button>
                        {canDeleteUnpublishedQuestions && question.status !== "Published" && (
                          <button
                            className="btn btn-red"
                            onClick={() => onDelete?.(question.id)}
                          >
                            <i className="f-icon trash-icon"></i>
                            {getResourceValue("delete", "Delete")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-data">
              {searchQuery.trim()
                ? getResourceValue("job_not_found", "Job not found.")
                : getResourceValue("no_questions_found", "No questions found")
              }
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JobQuestionsTable;
